import { Result, err, ok } from "../result";

const symbol: unique symbol = Symbol();

export type Opaque<TTag, TUnderlying> = TUnderlying & { readonly _: TTag };

export const make = <TTag, TUnderlying>(
  tag: TTag,
  body: TUnderlying
): Opaque<TTag, TUnderlying> => ({
  ...body,
  ["_"]: tag
});

export type GtinType = "GTIN8" | "GTIN12" | "GTIN13" | "GTIN14";

type GtinData = {
  readonly type: GtinType;

  readonly raw: string;
  readonly value: string;
  readonly normalized: string;
};

export type GTIN = Opaque<
  typeof symbol,
  {
    toString(): string;
    toRawString(): string;

    toGtin14(): GTIN;

    toObject(): GtinData;
    toJSON(): GtinData;
  }
>;

export type Error =
  | "invalid_length"
  | "invalid_characters"
  | "invalid_check_digit";

const lengths = [8, 12, 13, 14];

const sum = (a: number, b: number) => a + b;

const isCheckDigitValid = (gtin: string): boolean => {
  const check = parseInt(gtin[gtin.length - 1]);
  const sumOfDigits = gtin
    .split("", 13)
    .map(digit => parseInt(digit))
    .map((val, index) => (index % 2 === 0 ? val * 3 : val))
    .reduce(sum, 0);

  const calculated = (10 - (sumOfDigits % 10)) % 10;

  return calculated == check;
};

const determineType = (gtin: string): [string, GtinType] => {
  const gtin8 = gtin.replace(/^0+/, "").padStart(8, "0");

  if (gtin8.length === 8) {
    return [gtin8, "GTIN8"];
  }

  const gtin12 = gtin8.padStart(12, "0");

  if (gtin12.length === 12 && isCheckDigitValid(gtin12)) {
    return [gtin12, "GTIN12"];
  }

  const gtin13 = gtin12.padStart(13, "0");
  const gtin14 = gtin13.padStart(14, "0");

  if (gtin13.length === 13 && isCheckDigitValid(gtin14)) {
    return [gtin13, "GTIN13"];
  }

  return [gtin14, "GTIN14"];
};

const create = (
  raw: string,
  normalized: string,
  value: string,
  type: GtinType
): GTIN =>
  make(symbol, {
    toString() {
      return value;
    },

    toRawString() {
      return raw;
    },

    toGtin14() {
      return create(raw, normalized, normalized, "GTIN14");
    },

    toObject() {
      return {
        raw,
        normalized,

        value,
        type
      };
    },

    toJSON() {
      return this.toObject();
    }
  });

export const fromString = (input: string): Result<Error, GTIN> => {
  const cleaned = input.replace(/-/g, "");

  if (!/^\d+$/.test(cleaned)) {
    return err("invalid_characters");
  }

  if (!lengths.includes(cleaned.length)) {
    return err("invalid_length");
  }

  const normalized = cleaned.padStart(14, "0");

  const check = parseInt(normalized[normalized.length - 1]);
  const sumOfDigits = normalized
    .split("", 13)
    .map(digit => parseInt(digit))
    .map((val, index) => (index % 2 === 0 ? val * 3 : val))
    .reduce(sum, 0);

  const calculated = (10 - (sumOfDigits % 10)) % 10;

  if (check !== calculated) {
    return err("invalid_check_digit");
  }

  const [minimal, type] = determineType(normalized);

  return ok(create(cleaned, normalized, minimal, type));
};
